import { useEffect, useState } from 'react';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { fetchTaskLog } from '@src/api/taskLogger';

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '—';
}

/** Lazy-loads and renders a task run's log messages (most recent first).
 *  Shared by the By-job drill-down and the All-runs table. */
export default function RunMessages({ taskId }) {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchTaskLog(taskId, { includeMessages: true })
      .then((res) => {
        if (active) setMessages(res?.data?.messages || []);
      })
      .catch(() => {
        if (active) setMessages([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [taskId]);

  if (loading) {
    return (
      <Stack alignItems="center" py={2}>
        <CircularProgress size={18} />
      </Stack>
    );
  }
  if (!messages.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No log messages recorded for this run.
      </Typography>
    );
  }
  return (
    <Stack spacing={1}>
      {messages
        .slice()
        .sort((a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf())
        .map((msg) => (
          <Box
            key={msg.id}
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1.5,
              bgcolor: theme.palette.background.paper,
            })}
          >
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(msg.created)}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {msg.message}
            </Typography>
          </Box>
        ))}
    </Stack>
  );
}
